import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { useLazyQuery } from "@apollo/client";

import {
  Device,
  DeviceMake,
  GetDeviceByIdQuery,
  GetDeviceByIdQueryVariables,
  Service,
} from "../../../API";
import { useCustomerIdGuard } from "../../../common/hooks/useCustomerIdGuard";
import { useGetServicesByNode } from "./useGetServicesByNode";
import useLocationFromCache from "../../../common/hooks/useLocationFromCache";
import { GET_DEVICE_BY_ID } from "../../../common/operations/queries";
import { errorNotification } from "../../../common/variables/notification";
import { useSelectedCustomerId } from "../../../common/variables/selectedCustomer";
import { useListenToDeviceHealthDataChange } from "../../deployment-health/hooks/useListenToDeviceHealthDataChange";
import { useListenToNodeOnlineStatusChange } from "../../deployment-health/hooks/useListenToNodeHealthDataChange";
import {
  IFireSmokeModel,
  IGasLeakModel,
  IGateGuardModel,
  IHardHatModel,
  ILiquidLeakModel,
  ITankLevelModel,
  ServiceTypeEnum,
} from "../../model-manager/variables/modelManager";
import {
  deviceDataVariable,
  deviceFormVariables,
  useDeviceDataState,
} from "../variables/devices";
import { IDeviceTableRow, IModel } from "./useDeviceTableRows";
import useGetDeviceMakeByModel from "./useGetDeviceMakeByModel";

export interface IJsonData {
  [key: string]:
    | IGasLeakModel
    | ILiquidLeakModel
    | IFireSmokeModel
    | IHardHatModel
    | ITankLevelModel
    | IGateGuardModel
    | string
    | boolean;
}

const useGetDeviceById = () => {
  const { deviceId = "" } = useParams();

  const selectedCustomerIdInDropdown = useSelectedCustomerId();

  const customerIdFromClaims = useCustomerIdGuard();

  const memoizedCustomerId = useMemo(() => {
    return selectedCustomerIdInDropdown || customerIdFromClaims;
  }, [customerIdFromClaims, selectedCustomerIdInDropdown]);

  const { getCachedLocation } = useLocationFromCache();

  const { fetchDeviceMakeByModel } = useGetDeviceMakeByModel();

  const deviceVariable = useDeviceDataState();

  const { fetchServicesByNodeFunc, data: servicesResponse } =
    useGetServicesByNode();

  const services = useMemo(() => {
    return servicesResponse?.getServicesByNode?.items ?? [];
  }, [servicesResponse]);

  const [serviceData, setServiceData] = useState<Service[]>([]);
  const [getServicesLoading, setGetServicesLoading] = useState(false);

  const { data: deviceHealthData, isOnline } =
    useListenToDeviceHealthDataChange();

  const { data: nodeData } = useListenToNodeOnlineStatusChange();

  const [getDevice, { data, loading: getDeviceLoading }] = useLazyQuery<
    GetDeviceByIdQuery,
    GetDeviceByIdQueryVariables
  >(GET_DEVICE_BY_ID, {
    fetchPolicy: "cache-first",
    nextFetchPolicy: "cache-first",
    returnPartialData: true,
    notifyOnNetworkStatusChange: false,
  });

  useEffect(() => {
    if (
      nodeData?.listenToNodeOnlineStatus?.nodeId?.replaceAll("_", "#") ===
        data?.getDeviceById?.nodeId &&
      nodeData?.listenToNodeOnlineStatus?.isOnline === false &&
      deviceVariable
    ) {
      deviceDataVariable({ ...deviceVariable, isOnline: false });
    }
  }, [nodeData]);

  useEffect(() => {
    const isDeviceDataFromSubscription =
      deviceHealthData?.listenToDeviceHealthData;

    if (!isDeviceDataFromSubscription || !deviceVariable) return;

    if (isDeviceDataFromSubscription.id === deviceVariable.rowId) {
      deviceDataVariable({ ...deviceVariable, isOnline: isOnline });
    }
  }, [deviceHealthData]);

  const fetchDevice = useCallback(async () => {
    if (!memoizedCustomerId || !deviceId) {
      return;
    }

    try {
      const deviceResponse = await getDevice({
        variables: {
          customerId: memoizedCustomerId,
          deviceId,
        },
      });

      const device = deviceResponse?.data?.getDeviceById;

      if (!device) {
        return;
      }

      updateDeviceFormState(device);

      const locationCache = getCachedLocation(device.locationId as string);

      setGetServicesLoading(true);

      const allServices = await fetchServicesByNodeFunc(device.nodeId);

      // Filter services to only those belonging to this device
      const deviceServices = filterModelsForDevice(allServices, device);

      setServiceData(deviceServices);

      const deviceMakeResponse = await fetchDeviceMakeByModel(
        device.makeModelId ?? ""
      );

      const result = buildDeviceTableRow(
        device,
        locationCache?.name ?? "",
        deviceServices,
        deviceMakeResponse?.data?.getDeviceMakeByModel
      );

      updateDeviceFormWithExtendedInfo(
        device,
        deviceMakeResponse?.data?.getDeviceMakeByModel
      );

      setGetServicesLoading(false);

      return result;
    } catch (error) {
      console.error(error);

      setGetServicesLoading(false);

      errorNotification();
    }
  }, [memoizedCustomerId, deviceId, services.length]);

  const updateDeviceFormState = useCallback((device: Device) => {
    deviceFormVariables({
      isDirty: false,
      cameraIpAddress: device.cameraIpAddress ?? "",
      deviceData: device.deviceData,
      deviceName: device.name,
      deviceType: device.makeModelId,
      location: {
        value: device.locationId ?? "",
        title: device.locationName ?? "",
      },
      node: {
        id: device.nodeId ?? "",
        name: device.nodeName ?? "",
      },
      id: device.id,
    });
  }, []);

  const filterModelsForDevice = useCallback(
    (allModels: Service[], device: Device) => {
      return allModels.filter(service => {
        // Legacy service item's deviceId field has no C# prefix e.g. DE#<>;
        const doesLegacyIdsMatch = service?.deviceId === device?.id;
        // Service item's deviceId field has C# prefix e.g. C#<>DE#<>; must remove before comparison
        const doesIdsMatch = service?.deviceId?.split("DE#")[1]
          ? `DE#${service.deviceId.split("DE#")[1]}` === device?.id
          : false;

        return doesLegacyIdsMatch || doesIdsMatch;
      });
    },
    []
  );

  const buildDeviceTableRow = useCallback(
    (
      device: Device,
      locationName: string,
      models: Service[],
      deviceMakeResponse?: DeviceMake
    ): IDeviceTableRow => {
      return {
        rowId: device.id as string,
        name: device.name as string,
        healthData: device.healthData,
        location: {
          id: device.locationId as string,
          name: locationName,
        },
        node: {
          id: device.nodeId as string,
          name: device.nodeName as string,
        },
        makeModelId: device?.makeModelId as string,
        models:
          models?.map(
            (service): IModel => ({
              serviceName: (service?.serviceType as ServiceTypeEnum) ?? "",
              modelAlertSettingsId: service?.modelAlertSettingsId ?? "",
              modelAlertSettings: service?.modelAlertSettings ?? undefined,
              serviceId: service?.id ?? "",
              nodeId: service?.nodeId ?? "",
              isRunning: service?.isRunning ?? false,
              configuration: service?.configuration ?? "",
            })
          ) ?? [],
        modelsToRun: (deviceMakeResponse?.modelsToRun as string[]) ?? [],
        rtspTemplate: deviceMakeResponse?.rtspTemplate ?? "",
        deviceData: device.deviceData,
        isOnline: device.healthData.isOnline,
        cameraIpAddress: device?.cameraIpAddress as string,
      };
    },
    []
  );

  const updateDeviceFormWithExtendedInfo = useCallback(
    (device: Device, deviceMakeResponse?: DeviceMake) => {
      const deviceMakePanTiltValue = deviceMakeResponse?.hasPanTilt;
      const deviceMakeHasZoomValue = deviceMakeResponse?.hasZoom ?? false;

      deviceFormVariables({
        isDirty: false,
        cameraIpAddress: device?.cameraIpAddress ?? "",
        deviceData: device.deviceData,
        deviceName: device.name ?? "",
        deviceType: device.makeModelId ?? "",
        location: {
          value: device.locationId ?? "",
          title: device.locationName ?? "",
        },
        node: {
          id: device.nodeId ?? "",
          name: device.nodeName ?? "",
        },
        rtspTemplate: deviceMakeResponse?.rtspTemplate ?? "",
        defaultUserPwd: deviceMakeResponse?.defaultUserPwd ?? "",
        deviceMakePanTiltValue,
        deviceMakeHasZoomValue,
        id: device.id,
      });
    },
    []
  );

  return {
    data,
    fetchDevice,
    loading: getDeviceLoading,
    getServicesLoading,
    isServiceLoaded: !!serviceData,
  };
};

export default useGetDeviceById;
